import { useRef, useEffect } from "react";
import styled from "styled-components";
import Colors from "../../ui/global/Colors";
import Fonts from "../../ui/global/Fonts";
import TabPanel from "../../ui/components/TabPanel";
import ProjectGrid, { Project } from "./ProjectGrid";
import LoadingSpinner from "../../ui/components/LoadingSpinner";

const Root = styled.div<{ $colormode: 'light' | 'dark'; }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    width: 100%;
    height: 100%;
    min-height: fit-content;
    color: ${props => props.$colormode === 'light' ? Colors.black : Colors.white};
    padding: 5px;
    gap: 10px;
    overflow: hidden;
    pointer-events: auto;
    z-index: 10;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    opacity: 0.6;
`;

const EmptyStateText = styled.p`
    font-size: 16px;
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    margin: 0;
`;

export interface GalleryProps {
    colormode: 'light' | 'dark';
    type: 'Game' | 'Art' | 'Animation';
    projects: Project[];
    loading: boolean;
    hasMore: boolean;
    onLoadMore: () => Promise<void>;
    onProjectClick: (project: Project) => void;
    activeTab?: string;
    onTabChange?: (tab: string) => void;
    tabs?: Array<{ name: string; icon?: string; disabled?: boolean }>;
    userLoading: boolean;
    pageSize: number;
}

const Gallery = ({ 
    colormode,
    type,
    projects,
    loading,
    hasMore,
    onLoadMore,
    onProjectClick,
    activeTab = 'My Projects',
    onTabChange = () => {},
    tabs = [
        { name: 'My Projects' },
        { name: 'Community' },
        // { name: 'Top' },
        // { name: 'Hot' },
        // { name: 'New' },
    ],
    userLoading,
    pageSize = 12
}: GalleryProps) => {
    const loadingRef = useRef(null);

    // Set up intersection observer for infinite scrolling
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 }
        );

        if (loadingRef.current) {
            observer.observe(loadingRef.current);
        }

        return () => observer.disconnect();
    }, [hasMore, loading, onLoadMore]);

    const showEmptyState = !loading && !userLoading && projects.length === 0;
    const friendlyType = type === 'Art' ? type.toLowerCase() : `${type.toLowerCase()}s`;

    return (
        <Root $colormode={colormode}>
            {!userLoading && (
                <TabPanel 
                    tabs={tabs}
                    onSelect={onTabChange}
                    defaultTabName={activeTab}
                    colorMode={colormode}
                    size='small'
                />
            )}
            
            {showEmptyState ? (
                <EmptyState>
                    <EmptyStateText>
                        {activeTab.includes('My') && `Your ${friendlyType} will appear here. Start creating!`}
                    </EmptyStateText>
                </EmptyState>
            ) : (
                <ProjectGrid 
                    type={type}
                    projects={projects} 
                    onProjectClick={onProjectClick}
                    colormode={colormode}
                    isCommunity={activeTab === 'Community'}
                    loading={loading}
                    pageSize={pageSize}
                />
            )}

            <div ref={loadingRef} style={{ height: 20 }} />
        </Root>
    );
};

export default Gallery;